import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img4 from '../images/asset-management.jpg';

const AssetManagement = () => {
    useDocTitle('Asset Management - TerraLucrum');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className="bg-terra-blue-900 text-white py-20">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Asset Management</h1>
                        <p className="text-xl lg:text-2xl mb-8">Maximizing the value and performance of every property</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Partner With Us
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <h2 className="text-3xl font-bold text-terra-blue-900 mb-6">Hands-On Ownership</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                TerraLucrum treats every property as a living investment. We actively manage operations
                                to grow net operating income, tighten expense control, and strengthen occupancy so each
                                asset performs at its full potential quarter after quarter.
                            </p>
                            <p className="text-lg text-gray-700">
                                Our approach pairs disciplined financial oversight with real attention to tenants and
                                the physical asset. By reinvesting strategically and staying close to local market
                                conditions, we build durable, long-term value for the investors who partner with us.
                            </p>
                        </div>
                        <div>
                            <img src={img4} alt="Real Estate Asset Management" className="w-full max-w-md mx-auto" />
                        </div>
                    </div>

                    {/* Framework Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Our Asset Management Framework</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Assess</h3>
                                <p className="text-gray-600">Evaluate property performance, expenses, and market position to find upside</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Plan</h3>
                                <p className="text-gray-600">Build a business plan with clear NOI targets and a capital roadmap</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Execute</h3>
                                <p className="text-gray-600">Run day-to-day operations, leasing, and improvements with precision</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Optimize</h3>
                                <p className="text-gray-600">Monitor results and refine the plan to compound long-term value</p>
                            </div>
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Asset Management Services</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-4">Property Operations</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Day-to-day management oversight</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Vendor and contractor coordination</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Preventive maintenance programs</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Operating expense control</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-4">Leasing &amp; Tenant Relations</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Lease negotiation and renewals</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Occupancy and retention strategies</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Tenant screening and onboarding</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Responsive tenant communication</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-4">Financial Reporting &amp; Budgeting</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Monthly performance reporting</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Annual operating budgets</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> NOI and cash flow tracking</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Investor distributions and updates</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-4">Value-Add &amp; Capital Projects</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Unit and common area renovations</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Capital improvement planning</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Energy efficiency upgrades</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Repositioning and rebranding</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Results Section
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Typical Results</h2>
                        <div className="grid md:grid-cols-3 gap-8 text-center">
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">10-20%</div>
                                <p className="text-gray-600">Growth in net operating income</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">5-15%</div>
                                <p className="text-gray-600">Reduction in operating expenses</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">95%+</div>
                                <p className="text-gray-600">Stabilized portfolio occupancy</p>
                            </div>
                        </div>
                    </div> */}

                    {/* CTA Section */}
                    <div className="text-center bg-terra-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Maximize Your Returns?</h2>
                        <p className="text-xl mb-8">Let TerraLucrum manage your property and unlock its full value.</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Get Started
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default AssetManagement;
